import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Check, XIcon, Star } from 'lucide-react';

const PRICING = [
  {
    name: 'Forex Made Simple',
    subtitle: 'Beginner Course — Start Here',
    price: 97, originalPrice: null,
    slug: 'forex-made-simple',
    link: '/forex-made-simple',
    features: [
      { text: '19 video lessons (full beginner curriculum)', included: true },
      { text: '3 training workbooks (PDF)', included: true },
      { text: 'MT4 platform setup & walkthrough', included: true },
      { text: 'Candlestick patterns & formations', included: true },
      { text: 'Support, resistance & trend lines', included: true },
      { text: 'Moving averages & Stochastic', included: true },
      { text: 'Fundamentals & how to place trades', included: true },
      { text: 'Lifetime access — no subscriptions', included: true },
      { text: 'Prerequisite for all advanced courses', included: true },
    ],
    popular: false,
    accent: 'beginner',
  },
];

export default function CoursesPage() {
  const { addItem, items } = useCart();

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="courses-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-amber-400/[0.03] blur-[150px]" />
        <div className="relative mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400/80 mb-3">Level 1 — Start Here</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
            Beginner <span className="text-amber-400">Courses</span>
          </h1>
          <p className="mt-5 text-sm sm:text-base text-white/40 max-w-2xl mx-auto leading-relaxed">
            Build your trading foundation. Master the fundamentals of forex with Hettie Nel's proven beginner curriculum — from chart reading and candle patterns to three complete beginner trading strategies you can apply immediately.
          </p>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-white/30">
            <span>Beginner Friendly</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>Lifetime Access</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>30-Day Money Back</span>
          </div>
        </div>
      </section>

      {/* Prerequisite Notice removed — this IS the beginner course */}

      {/* Pricing */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="pricing-section">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Beginner Course</h2>
          <p className="text-sm text-white/40 mb-4 text-center">One-time payment. Lifetime access. 30-day money-back guarantee.</p>
          <p className="text-xs text-center text-amber-500/80 mb-10">
            Ready for more? Continue to <a href="/intermediate" className="underline hover:text-amber-400">Intermediate</a> or <a href="/advanced" className="underline hover:text-amber-400">Advanced</a> courses.
          </p>
          <div className="grid grid-cols-1 gap-6 max-w-xl mx-auto">
            {PRICING.map(plan => {
              const inCart = items.some(i => i.slug === plan.slug);
              const isScalp = plan.accent === 'scalp';
              const isBeginner = plan.accent === 'beginner';
              return (
                <div key={plan.slug} className={`rounded-xl border p-6 flex flex-col ${plan.popular ? 'border-amber-400/30 bg-amber-400/[0.03] ring-1 ring-amber-400/10' : isScalp ? 'border-sky-400/30 bg-sky-400/[0.03] ring-1 ring-sky-400/10' : isBeginner ? 'border-emerald-400/30 bg-emerald-400/[0.03] ring-1 ring-emerald-400/10' : 'border-white/[0.06] bg-white/[0.02]'}`} data-testid={`pricing-${plan.slug}`}>
                  {isBeginner && <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400 mb-3">Start Here — Prerequisite</p>}
                  {isScalp && <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-400 mb-3">Scalping Strategy</p>}
                  {plan.popular && <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 mb-3">Most Popular — Day Trading</p>}
                  {!plan.popular && !isScalp && !isBeginner && plan.originalPrice > 1000 && <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 mb-3">Best Value — Save $1,440</p>}
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <p className="text-xs text-white/40 mt-0.5">{plan.subtitle}</p>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    {plan.originalPrice && <span className="text-sm text-white/30 line-through">${plan.originalPrice}</span>}
                  </div>
                  <p className="text-[10px] text-white/30 mt-1">One-time payment</p>
                  {plan.link ? (
                    <Link
                      to={plan.link}
                      className="mt-5 w-full py-2.5 rounded-lg text-sm font-semibold text-center transition-colors bg-emerald-400 text-black hover:bg-emerald-300"
                      data-testid={`pricing-link-${plan.slug}`}
                    >
                      View Course
                    </Link>
                  ) : (
                    <button
                      onClick={() => addItem({ slug: plan.slug, name: plan.name, price: plan.price, image_url: '', platform: 'MT5' })}
                      disabled={inCart}
                      className={`mt-5 w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${inCart ? 'bg-white/5 text-white/30' : isScalp ? 'bg-sky-400 text-black hover:bg-sky-300' : plan.popular ? 'bg-amber-400 text-black hover:bg-amber-300' : 'bg-amber-400/10 text-amber-400 border border-amber-400/20 hover:bg-amber-400/20'}`}
                      data-testid={`pricing-add-${plan.slug}`}
                    >
                      {inCart ? 'In Cart' : 'Get Started'}
                    </button>
                  )}
                  <ul className="mt-5 space-y-2 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2 text-xs ${f.included ? 'text-white/50' : 'text-white/20'}`}>
                        {f.included ? <Check size={13} className={`${isScalp ? 'text-sky-400' : isBeginner ? 'text-emerald-400' : 'text-amber-400'} mt-0.5 shrink-0`} /> : <XIcon size={13} className="text-white/15 mt-0.5 shrink-0" />}
                        <span className={f.included ? '' : 'line-through'}>{f.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="trust-section">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-bold text-white mb-4">Why Traders Trust Hettie Nel</h2>
          <ul className="space-y-3 text-sm text-white/50">
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 20+ years of active forex trading and one-on-one training</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 4.9/5 rating on Forex Peace Army (71+ verified reviews)</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 900+ students trained through personal sessions</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> The 9-Step System is her flagship methodology</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> Strategies tested through bull markets, bear markets, crashes, and recoveries</li>
          </ul>
          <blockquote className="mt-8 p-5 rounded-xl border border-white/[0.06] bg-white/[0.02]">
            <p className="text-sm text-white/60 italic leading-relaxed">"Hettie's 9-Step system changed how I trade. Before, I was guessing. Now I have a checklist. I don't trade unless every step is green. My win rate went from 40% to over 65% in three months."</p>
            <p className="mt-3 text-xs text-white/30">— Forex Peace Army Verified Review</p>
          </blockquote>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04] text-center" data-testid="courses-cta">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-bold text-white">Ready for the Next Level?</h2>
          <p className="mt-3 text-sm text-white/40">Once you've mastered the fundamentals, continue your journey with Hettie's intermediate and advanced systems.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/intermediate" className="px-6 py-3 rounded-lg bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300 transition-colors" data-testid="courses-cta-intermediate">
              Intermediate Courses
            </Link>
            <Link to="/advanced" className="px-6 py-3 rounded-lg border border-white/10 text-white/60 text-sm hover:border-white/20 hover:text-white transition-colors" data-testid="courses-cta-advanced">
              Advanced Courses
            </Link>
          </div>
          <p className="mt-4 text-[10px] text-white/20">Secure payment via Stripe - Instant download after purchase</p>
        </div>
      </section>
    </div>
  );
}
